import {
  Component,
  Input,
  ViewEncapsulation,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { LoadingStateService } from '../../../providers/loading-state/loading-state.service';

@Component({
  selector: 'tidy-message-input',
  templateUrl: 'message-input.html',
  styleUrls: ['./message-input.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MessageInputComponent),
      multi: true
    }
  ]
})
export class MessageInputComponent implements ControlValueAccessor {
  @ViewChild('inputElement') inputElement: ElementRef;
  
  @Input() isSendingMessage: boolean = false;
  @Input() placeholder: string = 'Send Message';
  @Input() icon: string = 'assets/svg/search.svg';
  @Input() fixedWidth: boolean = true;
  @Input() leftIconAction = null;
  @Input() rightIconAction = null;
  @Input() attachmentIcon: string;
  @Input() showBlackButtons: boolean;
  @Input() form: FormControl;
  @Output() valueChange = new EventEmitter<string>();

  value: string = '';
  isInputEmpty: boolean = true;
  onChange: any = () => {};
  onTouched: any = () => {};
  actionLoading: boolean = false;

  constructor(public loadingState: LoadingStateService) {}

  writeValue(value: string): void {
    this.value = value;
    this.isInputEmpty = !value;
    // Reset height when value is cleared
    if (!value) {
      setTimeout(() => {
        const textarea = this.inputElement?.nativeElement;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInputChange(value: string): void {
    this.value = value;
    this.isInputEmpty = !value;
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
    this.adjustTextareaHeight();
  }

  adjustTextareaHeight(): void {
    const textarea = this.inputElement.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  clearInput(): void {
    this.value = '';
    this.isInputEmpty = true;
    this.onChange('');
    this.onTouched();
    this.valueChange.emit('');
    
    // Reset textarea height to initial state
    const textarea = this.inputElement.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  async executeRightIconAction() {
    if (this.loadingState.loading || !this.rightIconAction || this.isInputEmpty) {
      return;
    }
    try {
      this.loadingState.loading = true;
      this.actionLoading = true;
      await this.rightIconAction();
      this.clearInput();
    } catch (err) {
      console.error(err);
    } finally {
      this.loadingState.loading = false;
      this.actionLoading = false;
    }
  }

  async executeLeftIconAction() {
    if (this.loadingState.loading || !this.leftIconAction) {
      return;
    }
    try {
      this.loadingState.loading = true;
      await this.leftIconAction();
    } catch (err) {
      console.error(err);
    } finally {
      this.loadingState.loading = false;
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && this.rightIconAction && !this.isInputEmpty && !this.loadingState.loading) {
      event.preventDefault();
      this.executeRightIconAction();
    }
  }

  focusInput() {
    setTimeout(() => {
      this.inputElement?.nativeElement?.focus();
    });
  }
}
